import { useEffect, type FC, type RefObject } from 'react';

import { _notNil } from '@/littledash';
import type { MetadataField } from '@/model/Metadata.model';
import InVivoError from '@/model/InVivoError';
import { ExceptionHandler } from '@/utils/ExceptionHandler';

import SpreadsheetV16 from '@/components/UI/SpreadSheet/SpreadsheetV16';
import type { SpreadsheetV16Settings } from '@/components/UI/SpreadSheet/SpreadsheetV16.model';
import type { HotTableRef } from '@/components/UI/SpreadSheet/HotTableV16';
import { timepointAfterPasteHandlerV16 } from '@/components/UI/SpreadSheet/editors-v16/TimepointEditorV16';
import { defaultMetadataToColumnSettingMapperV16 } from '@/support/hot-v16';

interface BulkSampleEditTableV16Props {
  data: Array<Record<string, unknown>>;
  settings: SpreadsheetV16Settings;
  innerRef: RefObject<HotTableRef>;
  metadata: Array<MetadataField>;
  setMetadata: (metadata: Array<MetadataField>) => void;
  setInvalid: (invalid: boolean) => void;
  maxRows: number;
  customAfterRenderer?: (
    td: HTMLTableCellElement,
    row: number,
    col: number,
    prop: string | number,
    value: unknown,
    cellProperties: object
  ) => void;
}

const BulkSampleEditTableV16: FC<BulkSampleEditTableV16Props> = ({
  data,
  settings,
  innerRef,
  metadata,
  setMetadata,
  setInvalid,
  maxRows,
  customAfterRenderer,
}) => {
  useEffect(() => {
    const hot = innerRef.current?.hotInstance;
    if (_notNil(hot)) {
      hot.addHook('afterPaste', timepointAfterPasteHandlerV16);

      return () => {
        try {
          if (!hot.isDestroyed) {
            hot.removeHook('afterPaste', timepointAfterPasteHandlerV16);
          }
        } catch (error) {
          ExceptionHandler.captureException(
            new InVivoError('Failed to remove timepoint paste hook during cleanup', {
              cause: error,
              slug: 'bulk-sample-edit-hot-cleanup-error-v16',
            })
          );
        }
      };
    }
  }, [innerRef]);

  return (
    <SpreadsheetV16
      className="pl4 pr4"
      testPrefix="bulk-sample-edit-v16"
      data={data}
      innerRef={innerRef}
      settings={settings}
      metadata={metadata}
      setMetadata={setMetadata}
      setInvalid={setInvalid}
      metadataToColumnSettingMapper={defaultMetadataToColumnSettingMapperV16}
      maxRows={maxRows}
      greedyValidateRow
      customAfterRenderer={customAfterRenderer}
    />
  );
};

export default BulkSampleEditTableV16;
